import { CAMERA_ANIM_MS, clampZoom, easeCamera, fitZoom, wheelZoom } from './graphCamera';
import { MAX_ZOOM, MIN_ZOOM } from './graphCanvasConfig';

/** Camera animation, fit / focus, link drawing and every pointer / keyboard handler of GraphCanvas, attached to its canvas. A plain factory called once from the effect. */
export function graphInteraction({
  s,
  camera,
  worker,
  live,
  props,
  reduceMotion,
  renderer,
  linkLine,
  requestRender,
  applyCamera,
  toWorld,
  posAttr,
  pick,
  applyEmphasis,
}) {
    // ── camera helpers ─────────────────────────────────────────────────────
    live.camAnim = 0;
    const animateCamera = (x, y, zoom, animate = true) => {
      cancelAnimationFrame(live.camAnim);
      const from = { x: camera.position.x, y: camera.position.y, zoom: s.zoom };
      const to = { x, y, zoom: clampZoom(zoom, MIN_ZOOM, MAX_ZOOM) };
      if (!animate || reduceMotion) {
        camera.position.x = to.x;
        camera.position.y = to.y;
        s.zoom = to.zoom;
        applyCamera();
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / CAMERA_ANIM_MS);
        const cam = easeCamera(from, to, t);
        camera.position.x = cam.x;
        camera.position.y = cam.y;
        s.zoom = cam.zoom;
        applyCamera();
        if (t < 1) live.camAnim = requestAnimationFrame(step);
      };
      live.camAnim = requestAnimationFrame(step);
    };

    const fitView = (animate = true, ids = null) => {
      const pos = posAttr();
      if (!pos || !s.nodes.length) return;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < s.nodes.length; i += 1) {
        if (ids && !ids.has(s.nodes[i].id)) continue;
        const x = pos.array[i * 3];
        const y = pos.array[i * 3 + 1];
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
      if (!Number.isFinite(minX)) return;
      const zoom = fitZoom(maxX - minX, maxY - minY, s.width, s.height, { maxZoom: 1.25 });
      animateCamera((minX + maxX) / 2, (minY + maxY) / 2, zoom, animate);
    };

    const focusNode = (id) => {
      const i = s.index.get(id);
      const pos = posAttr();
      if (i == null || !pos) return;
      animateCamera(pos.array[i * 3], pos.array[i * 3 + 1], Math.max(s.zoom, 1.4));
    };

    // ── link mode ──────────────────────────────────────────────────────────
    const startLink = (id, clientPoint) => {
      const i = s.index.get(id);
      const pos = posAttr();
      if (i == null || !pos) return;
      s.linkFrom = i;
      if (clientPoint) {
        const r = canvas.getBoundingClientRect();
        s.linkCursor = toWorld(clientPoint.x - r.left, clientPoint.y - r.top);
      } else {
        s.linkCursor = { x: pos.array[i * 3] + 60, y: pos.array[i * 3 + 1] };
      }
      linkLine.visible = true;
      canvas.style.cursor = 'crosshair';
      props.current.onLinkModeChange?.(s.nodes[i]);
      applyEmphasis();
    };

    const cancelLink = () => {
      if (s.linkFrom < 0) return;
      // A Shift-drag cancelled by Esc / right-click / a rebuild must not keep a
      // live drag behind it, or the rest of the gesture pans the camera.
      if (live.drag?.linking) live.drag = null;
      s.linkFrom = -1;
      linkLine.visible = false;
      canvas.style.cursor = 'grab';
      props.current.onLinkModeChange?.(null);
      applyEmphasis();
    };

    // ── input ──────────────────────────────────────────────────────────────
    const canvas = renderer.domElement;

    const local = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const setHovered = (i) => {
      if (i === s.hovered) return;
      s.hovered = i;
      if (s.linkFrom >= 0) canvas.style.cursor = i >= 0 && i !== s.linkFrom ? 'copy' : 'crosshair';
      else canvas.style.cursor = i >= 0 ? 'pointer' : live.drag ? 'grabbing' : 'grab';
      props.current.onHover?.(i >= 0 ? s.nodes[i] : null);
      applyEmphasis();
    };

    const onWheel = (e) => {
      e.preventDefault();
      s.userMoved = true;
      const p = local(e);
      const before = toWorld(p.x, p.y);
      s.zoom = wheelZoom(s.zoom, e.deltaY, MIN_ZOOM, MAX_ZOOM);
      camera.position.x = before.x - (p.x - s.width / 2) / s.zoom;
      camera.position.y = before.y + (p.y - s.height / 2) / s.zoom;
      applyCamera();
    };

    const finishLink = (e, target) => {
      const from = s.nodes[s.linkFrom];
      const to = s.nodes[target];
      cancelLink();
      if (from && to && from.id !== to.id) {
        props.current.onLinkDrop?.({ from, to, clientX: e.clientX, clientY: e.clientY });
      }
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      const p = local(e);
      const hit = pick(p.x, p.y);
      if (s.linkFrom >= 0) {
        // In link mode a click on another item completes the link; anywhere
        // else cancels it.
        if (hit >= 0 && hit !== s.linkFrom) finishLink(e, hit);
        else cancelLink();
        return;
      }
      canvas.setPointerCapture(e.pointerId);
      // Shift-drag from an item draws a link straight away.
      if (hit >= 0 && e.shiftKey) {
        startLink(s.nodes[hit].id, { x: e.clientX, y: e.clientY });
        live.drag = { start: p, last: p, node: -1, moved: true, linking: true };
        return;
      }
      live.drag = { start: p, last: p, node: hit, moved: false };
      canvas.style.cursor = hit >= 0 ? 'pointer' : 'grabbing';
    };

    const onMove = (e) => {
      const p = local(e);
      if (s.linkFrom >= 0) {
        s.linkCursor = toWorld(p.x, p.y);
        setHovered(pick(p.x, p.y));
        requestRender();
        return;
      }
      if (!live.drag) {
        setHovered(pick(p.x, p.y));
        return;
      }
      if (Math.hypot(p.x - live.drag.start.x, p.y - live.drag.start.y) > 3) live.drag.moved = true;
      if (live.drag.node >= 0 && live.drag.moved) {
        const w = toWorld(p.x, p.y);
        worker.postMessage({ type: 'drag', gen: live.generation, index: live.drag.node, x: w.x, y: w.y });
        setHovered(live.drag.node);
      } else if (live.drag.node < 0) {
        s.userMoved = true;
        camera.position.x -= (p.x - live.drag.last.x) / s.zoom;
        camera.position.y += (p.y - live.drag.last.y) / s.zoom;
        applyCamera();
      }
      live.drag.last = p;
    };

    const onUp = (e) => {
      if (!live.drag) return;
      if (live.drag.linking) {
        // Shift-drag released: over an item → link, elsewhere → cancel.
        live.drag = null;
        const p = local(e);
        const hit = pick(p.x, p.y);
        if (s.linkFrom >= 0 && hit >= 0 && hit !== s.linkFrom) finishLink(e, hit);
        else cancelLink();
        return;
      }
      if (live.drag.node >= 0 && live.drag.moved) worker.postMessage({ type: 'release', gen: live.generation, index: live.drag.node });
      if (!live.drag.moved) {
        const node = live.drag.node >= 0 ? s.nodes[live.drag.node] : null;
        props.current.onSelect?.(node);
      }
      live.drag = null;
      const p = local(e);
      setHovered(pick(p.x, p.y));
    };

    const onDouble = (e) => {
      if (s.linkFrom >= 0) return;
      const p = local(e);
      const hit = pick(p.x, p.y);
      if (hit >= 0) props.current.onOpen?.(s.nodes[hit]);
    };

    const onContext = (e) => {
      e.preventDefault();
      if (s.linkFrom >= 0) {
        cancelLink();
        return;
      }
      const p = local(e);
      const hit = pick(p.x, p.y);
      props.current.onContextMenu?.({ node: hit >= 0 ? s.nodes[hit] : null, clientX: e.clientX, clientY: e.clientY });
    };

    const onLeave = () => {
      if (!live.drag) setHovered(-1);
    };

    const onKey = (e) => {
      if (e.key === 'Escape' && s.linkFrom >= 0) {
        e.stopPropagation();
        cancelLink();
      }
    };

    canvas.style.cursor = 'grab';
    canvas.style.touchAction = 'none';
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('contextmenu', onContext);
    window.addEventListener('keydown', onKey, true);
    // A cancelled gesture (OS, touch, lost capture) must unpin the node and
    // clear the drag, or hover stays dead and the node stays frozen.
    const onCancel = () => {
      if (!live.drag) return;
      if (live.drag.linking) cancelLink();
      else if (live.drag.node >= 0) worker.postMessage({ type: 'release', gen: live.generation, index: live.drag.node });
      live.drag = null;
      setHovered(-1);
    };
    canvas.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('lostpointercapture', onCancel);
    canvas.addEventListener('dblclick', onDouble);

  return {
    animateCamera,
    fitView,
    focusNode,
    startLink,
    cancelLink,
    canvas,
    onWheel,
    onDown,
    onMove,
    onUp,
    onDouble,
    onContext,
    onLeave,
    onKey,
    onCancel,
  };
}
